import React, { useId, useRef, useState, useEffect, useCallback } from 'react';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { Image } from '../Image/Image';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import type { CtaAction } from '../../utils/ctaAction';
import './Carousel.scss';

/**
 * Auto-rotation cadence for the tabbed variant. Single source of truth for
 * the JS `setInterval` tick and the CSS `carousel-progress-fill` animation
 * — the value is injected inline onto `.carousel__tabbed-body` so the
 * progress bar and the auto-advance share one clock.
 */
const AUTO_ROTATE_MS = 5000;

/**
 * Module-scoped inline style so React doesn't allocate a fresh object on
 * every render. AUTO_ROTATE_MS is a compile-time constant, so the derived
 * CSS custom property is too.
 */
const TABBED_BODY_STYLE = {
  '--poly-duration-carousel-autoRotate': `${AUTO_ROTATE_MS}ms`,
} as React.CSSProperties;

/**
 * Carousel — horizontally-scrolling row of promo cards.
 *
 * Section header (badge, heading, subhead, optional CTA) above a scrollable
 * track of promo cards. Each card renders a portrait media image with a
 * bottom gradient fade, a title, and a description. Optional whole-card link
 * makes the card a click target.
 *
 * Two visual variants controlled by `cardStyle`:
 * - `default` — image → title → description
 * - `step`    — 48px numbered badge → image → title → description; step
 *              numbers auto-derive from position (1..N).
 *
 * Cards are flat, indexed props (item1Heading, item1ImageUrl, …) up to 6
 * cards. A slot renders when it has a heading, a description, or an image;
 * in the tabbed variant a slot also needs a heading or a description,
 * because a tab has to show a label. Prev/next
 * chevron controls scroll the track by ~one card width; scroll-snap
 * enforces alignment. The nav row appears above the track on desktop and
 * below (spread) on mobile via CSS order.
 */

/** Reconstructed card assembled from the flat props. */
export interface CarouselItem {
  heading?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  href?: string;
}

/** Flat, per-card props for slots 1-6. */
export interface CarouselSlotProps {
  /** Item 1 */
  item1Heading?: string;
  item1Description?: string;
  /** @polyType Url */
  item1ImageUrl?: string;
  item1ImageAlt?: string;
  /** @polyType Url */
  item1Href?: string;

  /** Item 2 */
  item2Heading?: string;
  item2Description?: string;
  /** @polyType Url */
  item2ImageUrl?: string;
  item2ImageAlt?: string;
  /** @polyType Url */
  item2Href?: string;

  /** Item 3 */
  item3Heading?: string;
  item3Description?: string;
  /** @polyType Url */
  item3ImageUrl?: string;
  item3ImageAlt?: string;
  /** @polyType Url */
  item3Href?: string;

  /** Item 4 */
  item4Heading?: string;
  item4Description?: string;
  /** @polyType Url */
  item4ImageUrl?: string;
  item4ImageAlt?: string;
  /** @polyType Url */
  item4Href?: string;

  /** Item 5 */
  item5Heading?: string;
  item5Description?: string;
  /** @polyType Url */
  item5ImageUrl?: string;
  item5ImageAlt?: string;
  /** @polyType Url */
  item5Href?: string;

  /** Item 6 */
  item6Heading?: string;
  item6Description?: string;
  /** @polyType Url */
  item6ImageUrl?: string;
  item6ImageAlt?: string;
  /** @polyType Url */
  item6Href?: string;
}

/**
 * @polySemanticUsage About this block: a horizontally scrollable container that displays a series
 * of content cards (image + text + optional CTA) in a sliding format. Displays 3 full cards with a
 * 4th "peekaboo" card to signal scrollability, and uses a 4x5 vertical aspect ratio for images.
 * Use it for showcasing multiple related items in limited vertical space.
 * A card needs a heading, a description, or an image to render; the heading is optional. When a
 * card has no heading and no description, set item{N}ImageAlt to what the card shows, since the
 * alt text is then the card's only name for screen readers; an image-only card without alt text is
 * not rendered. The tabbed variant needs a heading or
 * a description on every item, because each item is a tab with a visible label.
 */
export interface CarouselProps extends CarouselSlotProps {
  /** Section-level badge pill text (e.g. "Seller benefits") */
  sectionBadge?: string;
  /**
   * CustomIcon name rendered inside the badge pill (default: "star").
   * @polyType SelectList
   * @polyOptions Star=star,Bolt=bolt,Cart=cart,Storefront=storefront,Package=package,Analytics=analytics,Check=check,Lock=lock,Play=play,Language=language,Globe=globe
   * @polyDefault star
   * @polyTranslatable false
   */
  sectionBadgeIcon?: CustomIconName;
  /** Section heading (renders as h2) */
  heading?: string;
  /** Section subheading */
  subheading?: string;
  /**
   * Section-level CTA button label (rendered next to the heading)
   * @polyHelp Keep it to 2 to 5 words (about 25 characters, 40 at most). Translations can run longer, and long labels wrap onto extra lines.
   */
  sectionCtaText?: string;
  /**
   * Section-level CTA button href.
   * @polyType Url
   */
  sectionCtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link field.
   * @polyDefault link
   * @polyTranslatable false
   */
  sectionCtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  sectionCtaTargetPopupId?: string;
  /**
   * Card visual style. `default` renders promo cards with image + text.
   * `step` adds a 48px auto-numbered circle badge above the card title.
   * @polyOptions Default=default,Step=step
   */
  cardStyle?: 'default' | 'step';
  /**
   * Layout variant.
   * - `scroll` — horizontally-scrolling row of promo cards with prev/next chevrons.
   * - `tabbed` — single large image on top; clicking a text panel below swaps the image.
   *
   * @polyOptions Scroll=scroll,Tabbed=tabbed
   */
  variant?: 'scroll' | 'tabbed';
}

/**
 * Assemble a card from the flat props for a given slot (1-6).
 *
 * A slot counts as occupied when it carries a heading, a description, or an
 * image. Gating on the heading alone would silently drop migrated cards that
 * only have an image or body copy.
 */
function getItem(props: CarouselSlotProps, slot: 1 | 2 | 3 | 4 | 5 | 6): CarouselItem | null {
  const heading = props[`item${slot}Heading`];
  const description = props[`item${slot}Description`];
  const imageUrl = props[`item${slot}ImageUrl`];
  if (!heading && !description && !imageUrl) return null;
  return {
    heading,
    description,
    imageUrl,
    imageAlt: props[`item${slot}ImageAlt`],
    href: props[`item${slot}Href`],
  };
}

/** A tab must show a visible label, so a tabbed item needs a heading or a description. */
function hasText(item: CarouselItem): boolean {
  return !!(item.heading || item.description);
}

/** An image-only card is named only by its alt text; without it the card (and its link) has no accessible name. */
function hasAccessibleName(item: CarouselItem): boolean {
  return hasText(item) || !!item.imageAlt;
}

export const Carousel: React.FC<CarouselProps> = ({
  sectionBadge,
  sectionBadgeIcon,
  heading,
  subheading,
  sectionCtaText,
  sectionCtaHref,
  sectionCtaAction,
  sectionCtaTargetPopupId,
  cardStyle = 'default',
  variant = 'scroll',
  ...slotProps
}) => {
  const filledSlots = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => ({ slot, item: getItem(slotProps as CarouselSlotProps, slot) }))
    .filter((entry): entry is { slot: 1 | 2 | 3 | 4 | 5 | 6; item: CarouselItem } => entry.item !== null);
  const isTabbed = variant === 'tabbed';
  const items = filledSlots
    .map(entry => entry.item)
    .filter(item => (isTabbed ? hasText(item) : hasAccessibleName(item)));

  const unnamedSlots = filledSlots
    .filter(({ item }) => !hasAccessibleName(item))
    .map(({ slot }) => slot)
    .join(', ');
  const skippedTabSlots = isTabbed
    ? filledSlots.filter(({ item }) => !hasText(item)).map(({ slot }) => slot).join(', ')
    : '';

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (skippedTabSlots) {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn(
        `[Carousel] Item slot(s) ${skippedTabSlots} have no heading or description, so they are skipped in the tabbed variant. A tab needs a visible label.`
      );
    } else if (unnamedSlots) {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn(
        `[Carousel] Item slot(s) ${unnamedSlots} have an image but no heading, description, or image alt text, so they are skipped: the card would have no accessible name. Set item{N}ImageAlt to what the card shows.`
      );
    }
  }, [skippedTabSlots, unnamedSlots]);

  const trackRef = useRef<HTMLOListElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Tabbed variant state: which tab is currently active. The active tab's
  // media is displayed above the tablist; other tabs are keyboard-reachable
  // via ArrowLeft/ArrowRight/Home/End per WAI-ARIA tab authoring pattern.
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const instanceId = useId();

  // Auto-rotation for tabbed variant.
  // Pauses on hover/focus within the component (WCAG 2.2.2). `restartTrigger`
  // is bumped by manual tab selection (click or keyboard) so this effect
  // clears the current interval and starts a fresh one — that keeps the
  // JS tick and the CSS progress bar aligned to the same clock.
  const isPausedRef = useRef(false);
  const autoRotateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [progressKey, setProgressKey] = useState(0); // remounts the CSS progress bar
  const [restartTrigger, setRestartTrigger] = useState(0); // forces the effect below to re-run

  useEffect(() => {
    if (variant !== 'tabbed' || items.length <= 1) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    autoRotateTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      setActiveTabIndex(prev => (prev + 1) % items.length);
      setProgressKey(k => k + 1);
    }, AUTO_ROTATE_MS);

    return () => {
      if (autoRotateTimerRef.current) clearInterval(autoRotateTimerRef.current);
    };
  }, [variant, items.length, restartTrigger]);

  const handleTabbedMouseEnter = useCallback(() => { isPausedRef.current = true; }, []);
  const handleTabbedMouseLeave = useCallback(() => { isPausedRef.current = false; }, []);
  const handleTabbedFocusIn = useCallback(() => { isPausedRef.current = true; }, []);
  const handleTabbedFocusOut = useCallback((e: React.FocusEvent) => {
    // Only unpause if focus leaves the component entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      isPausedRef.current = false;
    }
  }, []);

  // Recompute the disabled state of the prev/next chevrons based on the
  // track's current scroll position. Called on mount, on scroll, and on
  // resize. The 1px slack on the next-comparison guards against sub-pixel
  // rounding where scrollLeft + clientWidth can lag scrollWidth by <1px
  // even when the last card is fully visible.
  const updateScrollButtons = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollPrev(track.scrollLeft > 0);
    // Detect "more to the right" from the last slide's geometry rather than
    // scrollWidth/clientWidth. The --scrolled left-bleed margin widens the
    // track's clientWidth mid-scroll, which made the clientWidth math report
    // "at end" prematurely — dropping the right fade (and disabling the next
    // chevron) the instant you started scrolling. Comparing the last slide's
    // right edge to the track's right edge is immune to that width change.
    const lastSlide = track.lastElementChild as HTMLElement | null;
    if (!lastSlide) {
      setCanScrollNext(false);
      return;
    }
    const trackRight = track.getBoundingClientRect().right;
    const lastRight = lastSlide.getBoundingClientRect().right;
    setCanScrollNext(lastRight > trackRight + 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      track.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons, items.length]);

  const scrollByDirection = (direction: 'prev' | 'next') => {
    const track = trackRef.current;
    if (!track) return;
    // Scroll by 80% of the visible width so the last-visible card stays as
    // a visual anchor after the animation completes.
    const amount = track.clientWidth * 0.8;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: direction === 'prev' ? -amount : amount,
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    });
  };

  // Empty state — no cards authored. Render a discoverable placeholder so
  // the misconfig is visible in DOM inspection (matches ContentGrid's
  // approach for invalid single-column icon layouts).
  if (items.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Carousel] No items provided — populate at least one item{N}Heading, item{N}Description, or item{N}ImageUrl slot.');
    }
    return (
      <div
        data-poly-error="carousel-empty"
        data-poly-error-detail="No item{N} slots populated"
        aria-hidden="true"
        hidden
      />
    );
  }

  const isStep = cardStyle === 'step';
  const modifierClass = isStep ? ' carousel--step' : '';
  const showNav = items.length > 1;

  // ============================================
  // TABBED VARIANT — image on top, tablist below.
  // Clicking a tab (or keyboarding to it) swaps the media above.
  // No chevrons in this variant; tab focus + selection are the only nav.
  // ============================================
  if (variant === 'tabbed') {
    const safeActiveIndex = Math.min(activeTabIndex, items.length - 1);

    // Manual tab selection: bump progressKey to remount the CSS progress
    // bar from 0, and bump restartTrigger to re-run the auto-rotate effect
    // so the JS interval restarts from the same tick.
    const selectTab = (idx: number) => {
      setActiveTabIndex(idx);
      setProgressKey(k => k + 1);
      setRestartTrigger(t => t + 1);
    };

    const handleTabKeyDown = (idx: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      let newIdx = idx;
      if (e.key === 'ArrowRight') newIdx = (idx + 1) % items.length;
      else if (e.key === 'ArrowLeft') newIdx = (idx - 1 + items.length) % items.length;
      else if (e.key === 'Home') newIdx = 0;
      else if (e.key === 'End') newIdx = items.length - 1;
      selectTab(newIdx);
      tabRefs.current[newIdx]?.focus();
    };

    return (
      <section
        className="carousel carousel--tabbed"
        aria-roledescription="carousel"
        aria-label={heading || undefined}
      >
        <div className="carousel__container">
          <SectionHeader
            badge={sectionBadge}
            badgeIcon={sectionBadgeIcon}
            heading={heading}
            subheading={subheading}
            ctaText={sectionCtaText}
            ctaHref={sectionCtaHref}
            ctaAction={sectionCtaAction}
            ctaTargetPopupId={sectionCtaTargetPopupId}
            className="carousel__header"
            stackAtPhone
          />

          <div
            className="carousel__tabbed-body"
            style={TABBED_BODY_STYLE}
            onMouseEnter={handleTabbedMouseEnter}
            onMouseLeave={handleTabbedMouseLeave}
            onFocus={handleTabbedFocusIn}
            onBlur={handleTabbedFocusOut}
          >
            <div
              id={`carousel-tabpanel-${instanceId}`}
              className="carousel__tabbed-image"
              role="tabpanel"
              aria-labelledby={`carousel-tab-${instanceId}-${safeActiveIndex}`}
              aria-live="off"
            >
              {items.map((item, idx) => (
                item.imageUrl && (
                  <div
                    key={idx}
                    className={`carousel__tabbed-image-slide${idx === safeActiveIndex ? ' carousel__tabbed-image-slide--active' : ''}`}
                    aria-hidden={idx !== safeActiveIndex}
                  >
                    <Image
                      url={item.imageUrl}
                      alt={idx === safeActiveIndex ? (item.imageAlt || item.heading || '') : ''}
                      objectFit="cover"
                    />
                  </div>
                )
              ))}
            </div>

            <div className="carousel__tabs" role="tablist" aria-label="Carousel items">
              {items.map((item, idx) => {
                const isActive = idx === safeActiveIndex;
                return (
                  <button
                    key={`carousel-tab-${idx}`}
                    type="button"
                    ref={el => { tabRefs.current[idx] = el; }}
                    id={`carousel-tab-${instanceId}-${idx}`}
                    className={`carousel__tab${isActive ? ' carousel__tab--active' : ''}`}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`carousel-tabpanel-${instanceId}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectTab(idx)}
                    onKeyDown={handleTabKeyDown(idx)}
                  >
                    {isActive && (
                      <span
                        key={`progress-${progressKey}`}
                        className="carousel__tab-progress"
                      />
                    )}
                    {item.heading && <h3 className="carousel__tab-title">{item.heading}</h3>}
                    {item.description && (
                      <p className="carousel__tab-description">{item.description}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ============================================
  // SCROLL VARIANT (default) — horizontally-scrolling promo cards.
  // ============================================
  return (
    <section
      className={`carousel${modifierClass}`}
      aria-roledescription="carousel"
      aria-label={heading || undefined}
    >
      <div className="carousel__container">
        <SectionHeader
          badge={sectionBadge}
          badgeIcon={sectionBadgeIcon}
          heading={heading}
          subheading={subheading}
          ctaText={sectionCtaText}
          ctaHref={sectionCtaHref}
          ctaAction={sectionCtaAction}
          ctaTargetPopupId={sectionCtaTargetPopupId}
          className="carousel__header"
          stackAtPhone
        />

        <div className="carousel__body">
          {showNav && (
            <div className="carousel__nav" role="group" aria-label="Carousel navigation">
              <button
                type="button"
                className="carousel__nav-button carousel__nav-button--prev"
                aria-label="Previous slide"
                onClick={() => scrollByDirection('prev')}
                disabled={!canScrollPrev}
              >
                <CustomIcon name="chevron-left-lg" size={32} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="carousel__nav-button carousel__nav-button--next"
                aria-label="Next slide"
                onClick={() => scrollByDirection('next')}
                disabled={!canScrollNext}
              >
                <CustomIcon name="chevron-right-lg" size={32} aria-hidden="true" />
              </button>
            </div>
          )}

          <div
            className={`carousel__track-wrapper${
              canScrollPrev ? ' carousel__track-wrapper--scrolled' : ''
            }`}
          >
            <ol className="carousel__track" ref={trackRef} role="list" aria-label="Carousel items">
            {items.map((item, index) => (
              <li key={`carousel-card-${index}`} className="carousel__slide">
                {renderCard(item, index, isStep)}
              </li>
            ))}
          </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Render a single promo card. When `item.href` is set the whole card is a
 * link (semantic `<a>`); otherwise it's a plain `<article>`. Step badges
 * only render in step mode and are decorative (aria-hidden). The content
 * area is omitted on an image-only card so it doesn't leave an empty padded
 * strip under the image.
 */
function renderCard(item: CarouselItem, index: number, isStep: boolean) {
  const showText = hasText(item);
  const rootClass = [
    'carousel__card',
    item.href && 'carousel__card--link',
    !showText && !isStep && 'carousel__card--media-only',
  ].filter(Boolean).join(' ');

  const cardBody = (
    <>
      {item.imageUrl && (
        <div className="carousel__card-media">
          <Image url={item.imageUrl} alt={item.imageAlt || item.heading || ''} objectFit="cover" />
        </div>
      )}
      {(isStep || showText) && (
        <div className="carousel__card-content">
          {isStep && (
            <span className="carousel__card-step" aria-hidden="true">
              {index + 1}
            </span>
          )}
          {showText && (
            <div className="carousel__card-title-block">
              {item.heading && <h3 className="carousel__card-title">{item.heading}</h3>}
              {item.description && (
                <p className="carousel__card-description">{item.description}</p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (item.href) {
    return (
      <a href={item.href} className={rootClass}>
        <article>{cardBody}</article>
      </a>
    );
  }
  return <article className={rootClass}>{cardBody}</article>;
}
